import * as Menu from '@radix-ui/react-dropdown-menu'
import { Plus } from 'lucide-react'
import { type ReactNode, useSyncExternalStore } from 'react'
import { MenuContent, menuItemClass } from '@/components/Menu'
import { useBoardCtx } from '@/board/context'
import { useI18n } from '@/i18n/i18n'
import { Avatar } from '@/components/Avatar'
import { sessionName } from './describe'

/** Choose which session (or a new one) receives these board messages. */
export function HandToAiMenu({
  messageIds,
  children,
  onDone,
}: {
  messageIds: string[]
  children: ReactNode
  onDone?: () => void
}) {
  const { sessions } = useBoardCtx()
  const { t } = useI18n()
  const snap = useSyncExternalStore(sessions.subscribe, sessions.getSnapshot)
  // Outside apps (MCP) are not handed messages: they work from their own side.
  const ordered = [...snap.sessions.filter((s) => !s.external)].sort((a, b) =>
    a.id === snap.current ? -1 : b.id === snap.current ? 1 : b.updatedAt - a.updatedAt,
  )
  /** A session id, or "new". */
  const fire = (id: string) => {
    sessions.fire(id, messageIds)
    onDone?.()
  }
  return (
    <Menu.Root modal={false}>
      <Menu.Trigger asChild>{children}</Menu.Trigger>
      <MenuContent>
        <Menu.Label className="px-2 pt-1 pb-1 text-[11px] font-medium text-muted">
          {t('hand.to')}
        </Menu.Label>
        {ordered.slice(0, 8).map((s) => (
          <Menu.Item
            key={s.id}
            className={menuItemClass}
            onSelect={() => {
              fire(s.id)
            }}
          >
            <Avatar actor={{ kind: 'session', id: s.id }} />
            <span className="truncate">{sessionName(s, t('session.untitled'))}</span>
            {s.id === snap.current && (
              <span className="ml-auto text-[11px] text-muted">{t('hand.current')}</span>
            )}
          </Menu.Item>
        ))}
        {ordered.length > 0 && <Menu.Separator className="my-1 h-px bg-line" />}
        <Menu.Item
          className={menuItemClass}
          onSelect={() => {
            fire('new')
          }}
        >
          <Plus size={14} className="text-muted" />
          {t('hand.newSession')}
        </Menu.Item>
      </MenuContent>
    </Menu.Root>
  )
}
